import React, { useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TenantHeader from './TenantHeader';
import TenantSidebar from './TenantSidebar';
import TenantFooter from './TenantFooter';

/**
 * TenantDashboardLayout
 *
 * Shell layout for all tenant-student dashboard pages.
 *
 * sidebarExpanded is lifted here so the main content margin stays in
 * perfect sync with the sidebar width at all times — no hardcoded
 * breakpoint margins that lag behind the toggle.
 *
 * Usage:
 *   <TenantDashboardLayout>
 *     <SomePage />
 *   </TenantDashboardLayout>
 */
const TenantDashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Paths where Header and Sidebar should be hidden
  const hideNavPaths = [
    '/techmcq',
    '/student/explore/:slug',
    '/student/skillgen-questions',
    '/student/launch/:track/:subtopic',
    '/launchpadpage',
    '/topicexplorer',
    '/topicexplorer/:slug',
    '/questions/:technology/:level',
    '/techgrid',
    '/elite-mastery',
    '/elite-mastery/:tech'
  ];

  const shouldHideNav = hideNavPaths.some(path => 
    matchPath({ path, end: true }, location.pathname)
  );

  /**
   * Lifted sidebar expanded/collapsed state.
   * Passed into <TenantSidebar> so sidebar width and content margin
   * are always perfectly in sync.
   */
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Sidebar widths must match TenantSidebar.jsx fixed-position values
  const SIDEBAR_EXPANDED_W  = 256; // px — w-64
  const SIDEBAR_COLLAPSED_W = 72;  // px — w-[72px]
  const sidebarW = shouldHideNav ? 0 : (sidebarExpanded ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W);

  return (
    <div className={`min-h-screen bg-bgMain text-body font-sans relative overflow-x-hidden flex flex-col ${shouldHideNav ? 'pt-0' : 'pt-[61px]'}`}>
      {!shouldHideNav && <TenantHeader onMenuToggle={() => setMobileOpen(true)} />}

      <div className="flex flex-1 relative">
        {/* ── Desktop sidebar (fixed-positioned inside, so zero flex width) ── */}
        {!shouldHideNav && (
          <div className="hidden md:block flex-shrink-0">
            <TenantSidebar
              expanded={sidebarExpanded}
              onExpandedChange={setSidebarExpanded}
            />
          </div>
        )}

        {/*
         * ── Main content area ──
         *
         * The sidebar is position:fixed so it contributes 0 width to
         * the flex row.  We apply margin-left via inline style (driven
         * directly by React state) so it is always perfectly in sync
         * with the sidebar width — no CSS attribute-selector lag or
         * specificity issues.
         */}
        <div
          className="flex-1 flex flex-col min-h-screen relative"
          style={{
            marginLeft: `${sidebarW}px`,
            width: `calc(100% - ${sidebarW}px)`,
            transition: 'margin-left 300ms ease, width 300ms ease',
          }}
        >
          {/* Decorative background orb */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48 pointer-events-none" />

          <main className={`flex-1 relative z-10 ${shouldHideNav ? 'p-0' : 'p-4 sm:p-6'}`}>{children}</main>
          {!shouldHideNav && <TenantFooter />}
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {!shouldHideNav && (
        <div
          className={`md:hidden fixed inset-0 z-50 transition ${
            mobileOpen ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
          aria-hidden={!mobileOpen}
        >
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-heading/40 backdrop-blur-sm transition-opacity ${
              mobileOpen ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer panel */}
          <aside
            className={`absolute left-0 top-0 h-full w-[280px] bg-bgSection shadow-2xl transform transition-transform duration-300 ${
              mobileOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
          >
            <button
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-lg hover:bg-bgMain transition z-10"
            >
              <XMarkIcon className="h-5 w-5 text-muted" />
            </button>
            <TenantSidebar
              variant="mobile"
              onRequestClose={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}
    </div>
  );
};

export default TenantDashboardLayout;
